// TP-Scout-Requests API client — AGENT side (M3).
//
// Sibling of services/scoutRequestService.js (the club-side M2 client) so the two
// build streams never collide on one file. Paths + shapes are the M1 contract
// (dashboard/scout_request_views.py + urls.py), verified against the backend
// worktree, NEVER guessed:
//   browse feed     → /dashboard/scout-requests/feed/            (public, club open)
//   recommend/my    → /dashboard/scout-requests/recommendations/
//   agency labels   → /dashboard/scout-requests/labels/
//   priority marks  → /dashboard/scout-requests/marks/{reqId}/[set/]
//
// PRIVACY (Invariant 2): the agent-facing serializers are a structural whitelist —
// they carry NO recommendation counts, NO other agents' recommendations, NO
// dismissal state. The only feedback field that exists is the derived `on_radar`
// bool on the agent's own recommendation. This client only ever reads those.
//
// Player picks for the recommend flow are keyed by `wyscout_player` (WSPlayer pk):
// both the roster endpoint (/dashboard/agent/players/) and the platform search
// (/dashboard/search/ type=players) expose `wyscout_id`, and the server resolves
// the "Agency player" source label through the wyscout↔TP link (D-007). The
// contract's alternative `tp_player` (users.Player pk) is not surfaced by either
// list endpoint, so the FE stays on the one id both zones share.
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_ENDPOINT;

const authApi = axios.create({ baseURL: API_BASE_URL, withCredentials: true });

// Same Bearer-token attachment the club-network / agent-tender clients use.
authApi.interceptors.request.use((config) => {
  try {
    const tokens = JSON.parse(localStorage.getItem("tokens") || "null");
    if (tokens?.access) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${tokens.access}`;
    }
  } catch (e) {
    /* no token — request proceeds and the server returns 401 */
  }
  return config;
});

const fail = (error) => ({
  success: false,
  status: error?.response?.status,
  // Preserve the whole error body too — the recommend endpoint returns
  // { cap_reached, used, cap } on a 400 the UI wants to read (calm capped state).
  data: error?.response?.data,
  error:
    error?.response?.data?.detail ||
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    (typeof error?.response?.data === "object"
      ? Object.values(error.response.data)[0]
      : null) ||
    "Something went wrong. Please try again.",
});

const listPagination = (data, params) => ({
  count: data?.count ?? (Array.isArray(data?.results) ? data.results.length : 0),
  next: data?.next ?? null,
  previous: data?.previous ?? null,
  currentPage: parseInt(
    (params instanceof URLSearchParams ? params.get("page") : params?.page) || "1",
    10,
  ),
});

const isCancel = (error) => error?.name === "CanceledError" || error?.name === "AbortError";

export const scoutAgentService = {
  // ── Browse feed (public, club shown openly — Invariant 1) ──
  // Server params: position, country, category, horizon, q/search, page, page_size.
  // Age / fee-frame / priority are refined client-side (the feed serializer carries
  // the frame + the marks endpoint carries priority; the list endpoint filters on
  // the four params above only).
  getFeed: async (params, signal) => {
    try {
      const res = await authApi.get("/dashboard/scout-requests/feed/", { params, signal });
      return {
        success: true,
        data: res.data.results || res.data || [],
        pagination: listPagination(res.data, params),
      };
    } catch (error) {
      if (isCancel(error)) throw error;
      return fail(error);
    }
  },

  getRequest: async (id, signal) => {
    try {
      const res = await authApi.get(`/dashboard/scout-requests/feed/${id}/`, { signal });
      return { success: true, data: res.data };
    } catch (error) {
      if (isCancel(error)) throw error;
      return fail(error);
    }
  },

  // Share an OPEN scout request into an existing chat as a compact card (design
  // 5d). The backend sends the Stream card; the sender must already be a member
  // of the channel (403 otherwise, surfaced calmly). `channelId` = a stream
  // channel id the sender is in.
  shareRequest: async (requestId, { channelId, text } = {}) => {
    try {
      const res = await authApi.post(
        `/dashboard/scout-requests/feed/${requestId}/share/`,
        { channel_id: channelId, text: text || "" },
      );
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  // ── My recommendations (only the derived on_radar signal) ──
  getMyRecommendations: async (params, signal) => {
    try {
      const res = await authApi.get("/dashboard/scout-requests/recommendations/", { params, signal });
      return {
        success: true,
        data: res.data.results || res.data || [],
        pagination: listPagination(res.data, params),
      };
    } catch (error) {
      if (isCancel(error)) throw error;
      return fail(error);
    }
  },

  // Submit a recommendation. Zone-1 roster picks pass `playerAccountId` (the
  // account_id the roster endpoint exposes → recommendable even without a Wyscout
  // link, M3.5); Zone-2 search picks pass `wyscoutPlayer` (WSPlayer pk). The server
  // computes + persists the source label + outside-fee-frame flag and enforces the
  // cap of 3 (a 400 with cap_reached is a calm state, not an error toast).
  recommend: async ({ requestId, playerAccountId, wyscoutPlayer, reason }) => {
    const payload = { request: requestId, reason };
    if (playerAccountId !== undefined && playerAccountId !== null) {
      payload.player_account_id = playerAccountId;
    } else if (wyscoutPlayer !== undefined && wyscoutPlayer !== null) {
      payload.wyscout_player = wyscoutPlayer;
    }
    try {
      const res = await authApi.post("/dashboard/scout-requests/recommendations/", payload);
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  // ── Agency labels (shared within the agency; club never sees — Invariant 2) ──
  getLabels: async (signal) => {
    try {
      const res = await authApi.get("/dashboard/scout-requests/labels/", { signal });
      return { success: true, data: Array.isArray(res.data) ? res.data : res.data?.results || [] };
    } catch (error) {
      if (isCancel(error)) throw error;
      return fail(error);
    }
  },

  createLabel: async ({ name, color }) => {
    try {
      const res = await authApi.post("/dashboard/scout-requests/labels/", { name, color });
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  updateLabel: async (id, payload) => {
    try {
      const res = await authApi.patch(`/dashboard/scout-requests/labels/${id}/`, payload);
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  // ── Priority + label marks on a received request (agency-internal) ──
  // The mark viewset is keyed by REQUEST id (pk = request). List returns every mark
  // the agency has set; the feed hook indexes them by request to paint pills.
  getMarks: async (signal) => {
    try {
      const res = await authApi.get("/dashboard/scout-requests/marks/", { signal });
      return { success: true, data: Array.isArray(res.data) ? res.data : res.data?.results || [] };
    } catch (error) {
      if (isCancel(error)) throw error;
      return fail(error);
    }
  },

  // priority: 1 | 2 | 3 | null (clear); label_ids: number[]. Either may be omitted —
  // the server treats an absent `priority` as "keep" and an absent `label_ids` as
  // "leave the labels untouched".
  setMark: async (requestId, { priority, labelIds } = {}) => {
    const body = {};
    if (priority !== undefined) body.priority = priority;
    if (labelIds !== undefined) body.label_ids = labelIds;
    try {
      const res = await authApi.put(
        `/dashboard/scout-requests/marks/${requestId}/set/`,
        body,
      );
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },
};

export default scoutAgentService;
